/**
 * KrutiKala header behaviour
 * Figma Make: src/components/Header.tsx
 *  - scrolled state after 48px (bg-ivory + hairline shadow)
 *  - hover-intent mega menu (120ms close delay)
 *  - right-side mobile drawer with overlay + body scroll lock
 *  - exposes --header-height / --header-bottom-position for other components
 */
class KKHeader extends HTMLElement {
  constructor() {
    super();
    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.setHeaderHeight = this.setHeaderHeight.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.closeTimer = null;
    this.activeKey = null;
    this.lastFocused = null;
  }

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.bar = this.querySelector('[data-kk-header-bar]');
    this.navItems = Array.from(this.querySelectorAll('[data-kk-nav-item]'));
    this.panels = Array.from(this.querySelectorAll('[data-kk-panel]'));
    this.overlay = this.querySelector('[data-kk-drawer-overlay]');
    this.drawer = this.querySelector('[data-kk-drawer]');
    this.openButton = this.querySelector('[data-kk-drawer-open]');
    this.closeButtons = Array.from(this.querySelectorAll('[data-kk-drawer-close]'));
    this.threshold = Number(this.dataset.scrollThreshold || 48);
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');

    if (this.section && this.dataset.sticky === 'true') {
      this.section.classList.add('kk-header-sticky');
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.setHeaderHeight);
    this.desktopQuery.addEventListener('change', this.onBreakpointChange);
    document.addEventListener('keydown', this.onKeydown);

    this.onScroll();
    this.setHeaderHeight();
    this.bindMegaMenu();
    this.bindDrawer();
    this.bindAccountMenu();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.setHeaderHeight);
    this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.onAccountDocumentClick) {
      document.removeEventListener('click', this.onAccountDocumentClick);
    }
    document.body.classList.remove('kk-drawer-open');
  }

  /* ---------------------------------------------------------------- scroll */
  onScroll() {
    this.classList.toggle('kk-header--scrolled', window.scrollY > this.threshold);
  }

  setHeaderHeight() {
    if (!this.section) return;
    const height = this.section.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
    const bottom = Math.max(0, Math.round(this.section.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
  }

  onBreakpointChange(event) {
    if (event.matches) {
      this.closeDrawer();
    } else {
      this.closePanels();
    }
  }

  /* ------------------------------------------------------------- mega menu */
  bindMegaMenu() {
    this.navItems.forEach((item) => {
      const key = item.dataset.kkNavItem;
      const trigger = item.querySelector('[data-kk-nav-trigger]');
      if (!trigger) return;

      item.addEventListener('mouseenter', () => this.openPanel(key));
      item.addEventListener('mouseleave', () => this.scheduleClose());
      trigger.addEventListener('focus', () => this.openPanel(key));
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.openPanel(key);
          const first = this.getPanel(key)?.querySelector('a');
          if (first) first.focus();
        }
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget) && !this.getPanel(key)?.contains(event.relatedTarget)) {
          this.scheduleClose();
        }
      });
    });

    this.panels.forEach((panel) => {
      const key = panel.dataset.kkPanel;
      panel.addEventListener('mouseenter', () => this.openPanel(key));
      panel.addEventListener('mouseleave', () => this.scheduleClose());
      panel.addEventListener('focusout', (event) => {
        if (!panel.contains(event.relatedTarget) && !this.getTrigger(key)?.contains(event.relatedTarget)) {
          this.scheduleClose();
        }
      });
    });
  }

  getPanel(key) {
    return this.panels.find((panel) => panel.dataset.kkPanel === key) || null;
  }

  getTrigger(key) {
    const item = this.navItems.find((navItem) => navItem.dataset.kkNavItem === key);
    return item ? item.querySelector('[data-kk-nav-trigger]') : null;
  }

  openPanel(key) {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (!this.desktopQuery.matches) return;
    if (this.activeKey === key) return;

    this.closePanels(false);
    const panel = this.getPanel(key);
    const trigger = this.getTrigger(key);
    if (!panel) return;

    panel.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    this.activeKey = key;
  }

  scheduleClose() {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closePanels(), 120);
  }

  closePanels(clearTimer = true) {
    if (clearTimer && this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.panels.forEach((panel) => panel.classList.remove('is-open'));
    this.navItems.forEach((item) => {
      const trigger = item.querySelector('[data-kk-nav-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
    this.activeKey = null;
  }

  /* ---------------------------------------------------------------- drawer */
  bindDrawer() {
    if (!this.drawer) return;
    if (this.openButton) this.openButton.addEventListener('click', () => this.openDrawer());
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeDrawer()));
    if (this.overlay) this.overlay.addEventListener('click', () => this.closeDrawer());
  }

  /* ----------------------------------------------------------- account menu */
  bindAccountMenu() {
    this.accountMenu = this.querySelector('[data-kk-account-menu]');
    if (!this.accountMenu) return;

    this.accountToggle = this.accountMenu.querySelector('[data-kk-account-toggle]');
    this.accountPanel = this.accountMenu.querySelector('[data-kk-account-panel]');
    this.onAccountDocumentClick = this.onAccountDocumentClick.bind(this);

    if (this.accountToggle) {
      this.accountToggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleAccountMenu();
      });
    }

    this.accountMenu.addEventListener('mouseenter', () => {
      window.clearTimeout(this.accountCloseTimer);
      this.openAccountMenu();
    });
    this.accountMenu.addEventListener('mouseleave', () => {
      this.accountCloseTimer = window.setTimeout(() => this.closeAccountMenu(), 160);
    });

    document.addEventListener('click', this.onAccountDocumentClick);
  }

  toggleAccountMenu() {
    if (!this.accountMenu) return;
    if (this.accountMenu.classList.contains('is-open')) {
      this.closeAccountMenu();
    } else {
      this.openAccountMenu();
    }
  }

  openAccountMenu() {
    if (!this.accountMenu) return;
    this.accountMenu.classList.add('is-open');
    if (this.accountToggle) this.accountToggle.setAttribute('aria-expanded', 'true');
  }

  closeAccountMenu() {
    if (!this.accountMenu || !this.accountMenu.classList.contains('is-open')) return;
    this.accountMenu.classList.remove('is-open');
    if (this.accountToggle) this.accountToggle.setAttribute('aria-expanded', 'false');
  }

  onAccountDocumentClick(event) {
    if (!this.accountMenu || this.accountMenu.contains(event.target)) return;
    this.closeAccountMenu();
  }

  openDrawer() {
    if (!this.drawer) return;
    this.lastFocused = document.activeElement;
    this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    if (this.openButton) this.openButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('kk-drawer-open');
    const firstFocusable = this.drawer.querySelector('button, a, [tabindex="0"]');
    if (firstFocusable) firstFocusable.focus();
  }

  closeDrawer() {
    if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    if (this.openButton) this.openButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('kk-drawer-open');
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.drawer && this.drawer.classList.contains('is-open')) {
      this.closeDrawer();
      return;
    }
    if (this.accountMenu && this.accountMenu.classList.contains('is-open')) {
      this.closeAccountMenu();
      if (this.accountToggle) this.accountToggle.focus();
      return;
    }
    if (this.activeKey !== null) {
      const trigger = this.getTrigger(this.activeKey);
      this.closePanels();
      if (trigger) trigger.focus();
    }
  }
}

if (!customElements.get('kk-header')) {
  customElements.define('kk-header', KKHeader);
}
